import React, { useState, useMemo, Dispatch, SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { payCard } from '../../api/payCard'
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation'
import { extraFieldShowFilter } from '../../utils/filters'
import { paymentErrorHandler } from '../../utils/paymentErrorHandler'
import { redirectToAcs } from '../../utils/redirectToAcs'
import { loadExtraFieldsFromStorage, saveFieldValueToStorage } from '../../utils/extraFieldsStorage'
import { validateCardNumber, validateExpiryDate, validateCVV, validateCardholderName } from '../../utils/validations'
import { PaymentMethodType, PaymentPageInfo } from '../../types/paymentPageInfo'
import { getPaymentMethodExtraField } from '../../types/paymentMethod'
import CardData from '../../types/CardData'
import Money from '../../types/Money'
import ExtraFields from '../ExtraFields/ExtraFields'
import SubmitButton from '../SubmitButton/SubmitButton'
import InputForm from '../InputForm/InputForm'
import styles from './PaymentMethod.module.css'

interface CardPaymentProps {
    paymentPageInfo: PaymentPageInfo
    paymentMethod: PaymentMethodType
    money?: Money,
    email: string
    setParentError: (field: string, message: string) => void
    validateParentFields: (fields: Record<string, string>) => boolean
    setPaymentError: Dispatch<SetStateAction<boolean>>
}

const CardPaymentMethod = ({
    paymentPageInfo,
    paymentMethod,
    money,
    email,
    setParentError,
    validateParentFields,
    setPaymentError,
}: CardPaymentProps) => {
    const navigate = useNavigate()

    const [cardData, setCardData] = useState<CardData>({
        pan: '',
        exp: '',
        cvv: '',
        name: '',
    })

    const [extraFieldsData, setExtraFieldsData] = useState<Record<string, string>>(() => {
        const initialData: Record<string, string> = {}
        const storedData = loadExtraFieldsFromStorage(paymentPageInfo.id)

        paymentMethod.extraFields.forEach(field => {
            // Пустая строка из storedData приоритетнее значения из paymentMethod
            initialData[field.name] = storedData[field.name] ?? (field.value || '')
        })

        return initialData
    })

    const [isSubmitting, setIsSubmitting] = useState(false)

    const validationRules = useMemo(() => {
        const rules: ValidationRules = {
            pan: {
                required: true,
                validation: validateCardNumber,
            },
            exp: {
                required: true,
                validation: validateExpiryDate,
            },
            cvv: {
                required: true,
                validation: validateCVV,
            },
            name: {
                required: true,
                validation: validateCardholderName,
            },
        }

        paymentMethod.extraFields
            .filter(extraFieldShowFilter)
            .forEach(field => {
                rules[field.name] = {
                    required: true,
                    validation: getPaymentMethodExtraField(field.name)?.validation,
                }
            })

        return rules
    }, [paymentMethod.extraFields])

    const { errors, setError, removeError, validateAllFields } = useFormValidation(validationRules)

    const handleCardDataChange = (name: string, value: string) => {
        setCardData(prev => ({ ...prev, [name]: value }))
        removeError(name)
    }

    const handleExtraFieldChange = (name: string, value: string) => {
        saveFieldValueToStorage(paymentPageInfo.id, name, value)
        setExtraFieldsData(prev => ({ ...prev, [name]: value }))
        removeError(name)
    }

    const formatPan = (value: string) => {
        return value.replace(/[^0-9a-z]/gi, '').split(/(.{4})/).filter(Boolean).join(' ')
    }

    const formatExp = (value: string) => {
        return value.replace(/\D/g, '').split(/(.{2})/).filter(Boolean).join('/')
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const validParentFields = validateParentFields({ email: email, amount: money?.amount || '' })
        const validThisFields = validateAllFields({ ...cardData, ...extraFieldsData })

        if (!validParentFields || !validThisFields) {
            return
        }

        setIsSubmitting(true)

        try {
            const result = await payCard(paymentPageInfo.id, cardData, { email: email, ...extraFieldsData }, money)

            if (result.acsUrl) {
                redirectToAcs(result)
            } else {
                if (paymentPageInfo.skipResultPage && paymentPageInfo.redirectUrl) {
                    window.location.href = paymentPageInfo.redirectUrl
                } else {
                    navigate(`/result/${paymentPageInfo.id}/${result.transactionId}`)
                }
            }
        } catch (error) {
            paymentErrorHandler(error, setError, setParentError, setPaymentError)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>{t`Данные карты`}</h2>

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.cardSection}>
                    <InputForm
                        label={t`Номер карты *`}
                        type="text"
                        name="pan"
                        value={cardData.pan}
                        placeholder="1234 5678 9012 3456"
                        onChange={(e) => handleCardDataChange('pan', formatPan(e.target.value))}
                        maxLength={23}
                        errorMessage={errors.pan || undefined}
                    />

                    <div className={styles.cardRow}>
                        <InputForm
                            label={t`Срок действия *`}
                            type="text"
                            name="exp"
                            value={cardData.exp}
                            placeholder="MM/YY"
                            onChange={(e) => handleCardDataChange('exp', formatExp(e.target.value))}
                            maxLength={5}
                            errorMessage={errors.exp || undefined}
                        />
                        <InputForm
                            label="CVV *"
                            type="text"
                            name="cvv"
                            value={cardData.cvv}
                            placeholder="123"
                            onChange={(e) => handleCardDataChange('cvv', e.target.value.replace(/\D/g, ''))}
                            maxLength={4}
                            errorMessage={errors.cvv || undefined}
                        />
                    </div>

                    <InputForm
                        label={t`Имя на карте *`}
                        type="text"
                        name="name"
                        value={cardData.name}
                        placeholder={t`IVAN PETROV`}
                        onChange={(e) => handleCardDataChange('name', e.target.value.toUpperCase())}
                        maxLength={60}
                        errorMessage={errors.name || undefined}
                    />
                </div>

                {paymentMethod.extraFields.length > 0 && (
                    <ExtraFields
                        extraFields={paymentMethod.extraFields}
                        data={extraFieldsData}
                        errors={errors}
                        handleExtraFieldChange={handleExtraFieldChange}
                    />
                )}

                <SubmitButton isSubmitting={isSubmitting} />
            </form>
        </div>
    )
}

export default CardPaymentMethod
